import React, { useState, useEffect, useRef } from 'react';
import authService from '../services/authService';
import API_URL from '../config';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import StateBlock from './ui/StateBlock';
import ConfirmModal from './ui/ConfirmModal';
import { maskCNPJ, maskTelefone, maskCEP, isValidCNPJ } from '../utils/masks';
import UpsellPro from './UpsellPro'; // #323 — upsell no lugar dos alertas de gestão bloqueados por tier
import './AccountSettings.css';

const TABS = [
  { id: 'empresa', label: 'Empresa', icon: 'business' },
  { id: 'usuarios', label: 'Usuários', icon: 'group' },
  { id: 'certificacoes', label: 'Certificações', icon: 'verified' },
  { id: 'numeracao', label: 'Numeração de NCs', icon: 'tag' },
  // #67 (Felipe N2.1) — rótulos/cores de severidade da casa
  { id: 'severidade', label: 'Severidade', icon: 'label' },
  // Sessão 24 #59 — Felipe F1.3 Sessão 3: destinatários de alertas
  { id: 'alertas', label: 'Alertas', icon: 'notifications' },
  { id: 'assinatura', label: 'Assinatura', icon: 'credit_card' },
  { id: 'consultor', label: 'Consultor', icon: 'handshake' },
];

const ESTADOS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

export default function AccountSettings() {
  const [activeTab, setActiveTab] = useState('empresa');
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [assinandoPlano, setAssinandoPlano] = useState(null);  // #303: plano cujo checkout está em curso
  const [confirmAssinarPlano, setConfirmAssinarPlano] = useState(null);  // plano aguardando confirmação antes de ir pro pagamento
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef();
  // Sessão 24 #59 — bug fix: antes lia user só do localStorage,
  // que pode estar stale se getCurrentUser nunca foi salvo. Agora
  // refresh via API no mount + persiste no localStorage.
  const [user, setUserState] = useState(authService.getUser());
  const isAdmin = user?.role === 'admin';
  // #323 — Alertas de GESTÃO (CC casa cheia + lembrete de eficácia) são perk Pro+.
  // Sinal read-only do /auth/me (default true; o gate autoritativo é o cron no
  // backend, que só dispara pra Pro+). false → substitui a aba pelo upsell. Com
  // GATING_FEATURES_HABILITADO OFF, o backend devolve true ⇒ inerte.
  const alertasGestaoLiberado = user?.alertas_gestao_liberado !== false;
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const fresh = await authService.getCurrentUser();
        if (cancelado || !fresh) return;
        setUserState(fresh);
        // Persiste localStorage pra outras telas pegarem state atual
        localStorage.setItem('user', JSON.stringify(fresh));
      } catch (e) {}
    })();
    return () => { cancelado = true; };
  }, []);

  // #181: setores vêm da lista do backend (fonte única — inclui serviços como
  // Contábil/Fiscal). Antes o <select> de Indústria era hardcoded só industrial.
  const [industrias, setIndustrias] = useState([]);

  // Formulários
  const [empresaForm, setEmpresaForm] = useState({});
  const [newUser, setNewUser] = useState({ email: '', nome: '', senha: '', role: 'operador' });
  // #81 — convite de equipe por email
  const [invites, setInvites] = useState([]);
  const [newInvite, setNewInvite] = useState({ email: '', role: 'operador' });
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [newCert, setNewCert] = useState({ certificacao: '', orgao_certificador: '', numero_certificado: '', validade: '' });
  const [passwordForm, setPasswordForm] = useState({ user_id: null, nova_senha: '' });

  // C1 — Numeração de NCs
  const [ncNumbering, setNcNumbering] = useState(null);
  const [numberingForm, setNumberingForm] = useState({ formato: '', proximo_numero: '', continuo: false });

  // #67 — Rótulos/cores de severidade. Array [{nivel, rotulo, cor, corAtiva}]
  // na ordem pior→leve. corAtiva=false → cor default do tema (não enviada).
  const [severidadeForm, setSeveridadeForm] = useState(null);

  // Sessão 24 #59 — Felipe F1.3 Sessão 3: destinatários de alertas
  // Lista de e-mails que recebem CC dos alertas de ações em atraso
  // (D-3 / D-0 / D+1). Cron diário backend lê e dispara.
  const [destinatariosAlertas, setDestinatariosAlertas] = useState([]);
  const [novoDestinatario, setNovoDestinatario] = useState('');

  // #104 (Sessão 32): destinatários GERENCIAIS do lembrete de avaliação de
  // eficácia (público diferente do operacional acima). Se vazio, cai pro admin.
  const [destinatariosEficacia, setDestinatariosEficacia] = useState([]);
  const [novoDestinatarioEficacia, setNovoDestinatarioEficacia] = useState('');

  // #229 — Consultor externo (tab lazy-loaded quando a aba é aberta)
  const [consultorAccess, setConsultorAccess] = useState(null);
  const [consultorAccessLoading, setConsultorAccessLoading] = useState(false);
  const [consultorAccessSaving, setConsultorAccessSaving] = useState(false);

  // #243 follow-up — opt-in de auto-serviço do relógio de SLA 8D.
  // {sla_8d_habilitado, elegivel} do GET /api/account/sla-8d; null = não carregado.
  // `elegivel` vem do backend (fonte única do gate de tier Pro+) — o front só
  // decide a renderização: toggle pra elegível, linha de upsell pro resto.
  const [sla8d, setSla8d] = useState(null);
  const [sla8dSaving, setSla8dSaving] = useState(false);

  useEffect(() => {
    loadProfile();
    loadSubscription();
    loadNcNumbering();
    loadSeveridadeConfig();
    loadDestinatariosAlertas();
    loadDestinatariosEficacia();
    loadSla8d();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #229 — Lazy load do estado de acesso ao consultor (só quando a aba é aberta)
  useEffect(() => {
    if (activeTab !== 'consultor' || !isAdmin || consultorAccess !== null) return;
    setConsultorAccessLoading(true);
    fetch(`${API_URL}/api/account/consultor-access`, { headers: authService.getAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => setConsultorAccess(data))
      .catch(() => setConsultorAccess({ _error: true }))
      .finally(() => setConsultorAccessLoading(false));
  }, [activeTab, isAdmin, consultorAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // #81 — carrega convites pendentes ao abrir a aba Usuários (admin).
  useEffect(() => {
    if (activeTab === 'usuarios' && isAdmin) loadInvites();
  }, [activeTab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleConsultorAcesso = async (novoValor) => {
    setConsultorAccessSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/account/consultor-access`, {
        method: 'PATCH',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ consultor_pode_ver_ncs: novoValor }),
      });
      if (!res.ok) throw new Error('Erro ao atualizar');
      const data = await res.json();
      setConsultorAccess(prev => ({ ...prev, consultor_pode_ver_ncs: data.consultor_pode_ver_ncs }));
    } catch (e) {
      // sem-op — UI fica no valor anterior
    } finally {
      setConsultorAccessSaving(false);
    }
  };

  // #181: busca a lista de setores do backend (mesma fonte do dropdown da NC),
  // pra o setor do tenant nunca desencontrar da biblioteca (industry_context).
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/v2/nc/industrias`);
        const data = await res.json();
        setIndustrias(data.industrias || []);
      } catch (e) {
        console.error('Erro ao buscar setores:', e);
      }
    })();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/profile`, {
        headers: authService.getAuthHeaders()
      });
      const data = await res.json();
      setProfile(data);
      setEmpresaForm({
        razao_social: data.razao_social || '',
        nome_fantasia: data.nome_fantasia || '',
        cnpj: data.cnpj || '',
        inscricao_estadual: data.inscricao_estadual || '',
        telefone: data.telefone || '',
        website: data.website || '',
        industria: data.industria || '',  // Sprint 2 PT2
        endereco_logradouro: data.endereco?.logradouro || '',
        endereco_numero: data.endereco?.numero || '',
        endereco_complemento: data.endereco?.complemento || '',
        endereco_bairro: data.endereco?.bairro || '',
        endereco_cidade: data.endereco?.cidade || '',
        endereco_estado: data.endereco?.estado || '',
        endereco_cep: data.endereco?.cep || '',
      });
      if (data.logo_url) setLogoPreview(data.logo_url.startsWith('data:') ? data.logo_url : `${API_URL}/${data.logo_url}`);
    } catch (e) {
      showMessage('Erro ao carregar perfil', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/subscription`, {
        headers: authService.getAuthHeaders()
      });
      const data = await res.json();
      setSubscription(data);
    } catch (e) {}
  };

  // #303 — planos com checkout self-service (espelha PLANOS_SELF_SERVE do backend).
  // Os demais (Enterprise) seguem por contato comercial.
  const PLANOS_CHECKOUT = ['starter', 'pro'];

  const handleAssinar = async (planoKey) => {
    // Plano sem checkout self-service → contato comercial.
    if (!PLANOS_CHECKOUT.includes(planoKey)) {
      window.open('https://qaasa.com.br/planos#contato', '_blank', 'noopener');
      return;
    }
    setAssinandoPlano(planoKey);
    try {
      const res = await fetch(`${API_URL}/api/account/subscription/checkout`, {
        method: 'POST',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ plano: planoKey }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.checkout_url) {
        showMessage(data.detail || 'Não foi possível iniciar o checkout. Tente novamente.', 'error');
        setAssinandoPlano(null);
        return;
      }
      // Redireciona pra página de pagamento (Pix/boleto) do gateway. O plano é
      // ativado automaticamente pelo webhook quando o pagamento é confirmado.
      window.location.href = data.checkout_url;
    } catch (e) {
      showMessage('Erro ao iniciar o checkout. Tente novamente.', 'error');
      setAssinandoPlano(null);
    }
  };

  // Confirma a intenção antes de mandar pro gateway: o usuário sai do app pra uma
  // página de pagamento externa (Asaas) — o modal explica isso antes do redirect.
  // Em caso de sucesso o handleAssinar redireciona (a página sai); em falha ele já
  // mostra o erro. Fechamos o modal nos dois casos.
  const confirmarAssinatura = async () => {
    const key = confirmAssinarPlano;
    if (!key) return;
    await handleAssinar(key);
    setConfirmAssinarPlano(null);
  };

  const loadNcNumbering = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/nc-numbering`, {
        headers: authService.getAuthHeaders()
      });
      if (!res.ok) return;
      const data = await res.json();
      setNcNumbering(data);
      setNumberingForm({
        formato: data.formato || 'RNC-{YEAR}-{NUMBER:04d}',
        proximo_numero: String(data.proximo_numero || 1),
        continuo: !!data.continuo,  // #135
      });
    } catch (e) {}
  };

  const saveNcNumbering = async () => {
    setSaving(true);
    try {
      const body = {};
      if (numberingForm.formato) body.formato = numberingForm.formato;
      if (numberingForm.proximo_numero) {
        const n = parseInt(numberingForm.proximo_numero, 10);
        if (!Number.isNaN(n)) body.proximo_numero = n;
      }
      body.nc_numbering_continuous = !!numberingForm.continuo;  // #135
      const res = await fetch(`${API_URL}/api/account/nc-numbering`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage(data.detail || 'Erro ao salvar', 'error');
        return;
      }
      setNcNumbering(data);
      setNumberingForm({
        formato: data.formato,
        proximo_numero: String(data.proximo_numero),
        continuo: !!data.continuo,  // #135
      });
      showMessage('Configuração de numeração salva com sucesso!');
    } catch (e) {
      showMessage('Erro ao salvar numeração: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── #243 follow-up — Relógio de SLA 8D (opt-in, gated Pro+) ─────────────
  const loadSla8d = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/sla-8d`, {
        headers: authService.getAuthHeaders()
      });
      if (!res.ok) return;
      setSla8d(await res.json());
    } catch (e) {}
  };

  const toggleSla8d = async (habilitado) => {
    setSla8dSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/account/sla-8d`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ habilitado }),
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage(data.detail || 'Erro ao salvar', 'error');
        return;
      }
      setSla8d(data);
      showMessage(habilitado
        ? 'Relógio de SLA 8D ativado — os prazos valem para as NCs abertas e novas.'
        : 'Relógio de SLA 8D desativado.');
    } catch (e) {
      showMessage('Erro ao salvar: ' + e.message, 'error');
    } finally {
      setSla8dSaving(false);
    }
  };

  // ─── #67 — Rótulos/cores de severidade ───────────────────────────────────
  // Cor inicial do seletor quando o tenant ativa "cor personalizada" pela 1ª
  // vez (aproxima o token de tema atual; só vira valor real ao salvar com a
  // caixa marcada). Com a caixa desmarcada, o badge segue a cor default do CSS.
  const _CORES_PADRAO = { critica: '#c62828', alta: '#e8590c', media: '#f59f00', baixa: '#1565c0' };

  const loadSeveridadeConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/severidade-config`, {
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setSeveridadeForm((data.niveis || []).map((n) => ({
        nivel: n.nivel,
        rotulo: n.rotulo || '',
        corAtiva: !!n.cor,
        cor: n.cor || _CORES_PADRAO[n.nivel] || '#888888',
      })));
    } catch (e) {}
  };

  const saveSeveridadeConfig = async () => {
    if (!severidadeForm) return;
    setSaving(true);
    try {
      const niveis = {};
      for (const n of severidadeForm) {
        niveis[n.nivel] = { rotulo: (n.rotulo || '').trim() };
        if (n.corAtiva) niveis[n.nivel].cor = n.cor;
      }
      const res = await fetch(`${API_URL}/api/account/severidade-config`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ niveis }),
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage(data.detail || 'Erro ao salvar', 'error');
        return;
      }
      // Reflete a config salva no localStorage('user') pra as telas (lista,
      // detalhe) já lerem os novos rótulos/cores sem precisar relogar.
      try {
        const cached = JSON.parse(localStorage.getItem('user') || '{}');
        const map = {};
        for (const n of (data.niveis || [])) map[n.nivel] = { rotulo: n.rotulo, ...(n.cor ? { cor: n.cor } : {}) };
        cached.client_severidade_labels = map;
        localStorage.setItem('user', JSON.stringify(cached));
      } catch (e) {}
      showMessage('Escala de severidade salva com sucesso!');
    } catch (e) {
      showMessage('Erro ao salvar severidade: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Destinatários de alertas (Sessão 24 #59) ────────────────────────────

  const loadDestinatariosAlertas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/destinatarios-alertas`, {
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setDestinatariosAlertas(data.destinatarios || []);
    } catch (e) {}
  };

  const saveDestinatariosAlertas = async (novaLista) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/account/destinatarios-alertas`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ destinatarios: novaLista }),
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage(data.detail || 'Erro ao salvar destinatários', 'error');
        return false;
      }
      setDestinatariosAlertas(data.destinatarios || []);
      return true;
    } catch (e) {
      showMessage('Erro ao salvar destinatários: ' + e.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addDestinatarioAlerta = async () => {
    const email = (novoDestinatario || '').trim().toLowerCase();
    if (!email) return;
    if (!email.includes('@')) {
      showMessage('E-mail inválido — falta @', 'error');
      return;
    }
    if (destinatariosAlertas.includes(email)) {
      showMessage('Este e-mail já está na lista', 'error');
      return;
    }
    const novaLista = [...destinatariosAlertas, email];
    const ok = await saveDestinatariosAlertas(novaLista);
    if (ok) {
      setNovoDestinatario('');
      showMessage(`${email} adicionado aos destinatários de alertas.`);
    }
  };

  const removeDestinatarioAlerta = async (email) => {
    if (!window.confirm(`Remover ${email} dos destinatários de alertas?`)) return;
    const novaLista = destinatariosAlertas.filter((e) => e !== email);
    const ok = await saveDestinatariosAlertas(novaLista);
    if (ok) {
      showMessage(`${email} removido.`);
    }
  };

  // ─── Destinatários de eficácia (Sessão 32 #104 — alerta gerencial) ───────────

  const loadDestinatariosEficacia = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/destinatarios-eficacia`, {
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setDestinatariosEficacia(data.destinatarios || []);
    } catch (e) {}
  };

  const saveDestinatariosEficacia = async (novaLista) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/account/destinatarios-eficacia`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ destinatarios: novaLista }),
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage(data.detail || 'Erro ao salvar destinatários de eficácia', 'error');
        return false;
      }
      setDestinatariosEficacia(data.destinatarios || []);
      return true;
    } catch (e) {
      showMessage('Erro ao salvar destinatários de eficácia: ' + e.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addDestinatarioEficacia = async () => {
    const email = (novoDestinatarioEficacia || '').trim().toLowerCase();
    if (!email) return;
    if (!email.includes('@')) {
      showMessage('E-mail inválido — falta @', 'error');
      return;
    }
    if (destinatariosEficacia.includes(email)) {
      showMessage('Este e-mail já está na lista', 'error');
      return;
    }
    const novaLista = [...destinatariosEficacia, email];
    const ok = await saveDestinatariosEficacia(novaLista);
    if (ok) {
      setNovoDestinatarioEficacia('');
      showMessage(`${email} adicionado aos destinatários de eficácia.`);
    }
  };

  const removeDestinatarioEficacia = async (email) => {
    if (!window.confirm(`Remover ${email} dos destinatários de eficácia?`)) return;
    const novaLista = destinatariosEficacia.filter((e) => e !== email);
    const ok = await saveDestinatariosEficacia(novaLista);
    if (ok) {
      showMessage(`${email} removido.`);
    }
  };

  // Preview ao vivo do próximo número (sem chamar backend)
  const formatPreview = (template, ano, n) => {
    if (!template) return '';
    return template
      .replace('{NUMBER:04d}', String(n).padStart(4, '0'))
      .replace('{NUMBER:03d}', String(n).padStart(3, '0'))
      .replace('{NUMBER:02d}', String(n).padStart(2, '0'))
      .replace('{NUMBER}', String(n))
      .replace('{YEAR:02d}', String(ano).slice(-2))
      .replace('{YEAR}', String(ano));
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  // ── EMPRESA ──────────────────────────────────────────────────────────────

  // #231: máscara progressiva em CNPJ/telefone. O backend tolera valor
  // formatado (validate_cnpj remove não-dígitos), então gravamos o mascarado.
  const handleEmpresaChange = (e) => {
    const { name, value } = e.target;
    let masked = value;
    if (name === 'cnpj') masked = maskCNPJ(value);
    else if (name === 'telefone') masked = maskTelefone(value);
    else if (name === 'endereco_cep') masked = maskCEP(value);
    setEmpresaForm({ ...empresaForm, [name]: masked });
  };

  // #231: validação inline de CNPJ — só sinaliza quando preenchido e inválido
  // (campo é opcional; vazio não é erro). Espelha o algoritmo do backend.
  const cnpjInvalido = !!empresaForm.cnpj && !isValidCNPJ(empresaForm.cnpj);

  const saveEmpresa = async (e) => {
    e.preventDefault();
    // #231: bloquear submit com CNPJ preenchido e inválido (feedback inline já
    // sinaliza no campo). Vazio é permitido — o campo é opcional.
    if (empresaForm.cnpj && !isValidCNPJ(empresaForm.cnpj)) {
      showMessage('CNPJ inválido — corrija antes de salvar.', 'error');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      // FIX: pular undefined E null E string "null" — estavam sendo enviados como
      // "null" para o backend, gerando ruído nos logs e potencialmente 422 em
      // campos com validador Pydantic.
      Object.entries(empresaForm).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        const s = String(v);
        if (s === 'null' || s === 'undefined') return;
        fd.append(k, s);
      });

      const res = await fetch(`${API_URL}/api/account/profile`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: fd
      });

      let body = null;
      try { body = await res.json(); } catch (_) {}

      if (!res.ok) {
        throw new Error(body?.detail || `Erro ${res.status} ao salvar`);
      }
      showMessage('Dados da empresa salvos com sucesso!');
      loadProfile();
    } catch (err) {
      console.error('[Empresa save] erro:', err);
      const isNetworkError = err instanceof TypeError && /fetch/i.test(err.message);
      const msg = isNetworkError
        ? 'Falha de rede ao salvar os dados. Verifique sua conexão e tente novamente.'
        : (err.message || 'Erro ao salvar');
      showMessage(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // FIX: validar tamanho ANTES de mostrar preview/disparar upload
    if (file.size > 2 * 1024 * 1024) {
      showMessage(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo 2 MB.`, 'error');
      e.target.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);

    // Lê o arquivo em base64 (usado tanto no fallback quanto no path principal)
    const fileToDataUrl = (f) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

    // Tentativa 1: multipart/form-data (path original)
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch(`${API_URL}/api/account/logo`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: fd
      });

      let body = null;
      try { body = await res.json(); } catch (_) {}

      if (!res.ok) {
        throw new Error(body?.detail || `Erro ${res.status} ao enviar logo`);
      }

      showMessage('Logo atualizado!');
      if (body?.logo_url) setLogoPreview(body.logo_url);
      loadProfile();
      return;
    } catch (err) {
      const isNetworkError = err instanceof TypeError && /fetch/i.test(err.message);
      if (!isNetworkError) {
        // Erro real do backend — não tentar fallback, exibir e limpar
        console.error('[Logo upload] erro de backend:', err);
        showMessage(err.message || 'Erro ao enviar logo', 'error');
        setLogoPreview(null);
        e.target.value = '';
        return;
      }
      // Tentativa 2: fallback JSON-base64 (multipart pode estar bloqueado por
      // proxy corporativo, extensão de browser, etc.)
      console.warn('[Logo upload] multipart falhou, tentando fallback base64...');
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await fetch(`${API_URL}/api/account/logo-b64`, {
        method: 'POST',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ data_url: dataUrl })
      });
      let body = null;
      try { body = await res.json(); } catch (_) {}
      if (!res.ok) throw new Error(body?.detail || `Erro ${res.status} ao enviar logo (fallback)`);
      showMessage('Logo atualizado!');
      if (body?.logo_url) setLogoPreview(body.logo_url);
      loadProfile();
    } catch (err) {
      console.error('[Logo upload] fallback também falhou:', err);
      const isNet = err instanceof TypeError && /fetch/i.test(err.message);
      showMessage(
        isNet
          ? 'Falha de rede ao enviar o logo (multipart e base64). Verifique sua conexão, desative ad-blockers/VPN e tente novamente.'
          : (err.message || 'Erro ao enviar logo'),
        'error'
      );
      setLogoPreview(null);
      e.target.value = '';
    }
  };

  // ── USUÁRIOS ─────────────────────────────────────────────────────────────

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(newUser).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${API_URL}/api/account/users`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: fd
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      showMessage('Usuário criado com sucesso!');
      setNewUser({ email: '', nome: '', senha: '', role: 'operador' });
      loadProfile();
    } catch (e) {
      showMessage(e.message || 'Erro ao criar usuário', 'error');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (userId) => {
    if (!passwordForm.nova_senha || passwordForm.nova_senha.length < 6) {
      showMessage('Senha deve ter pelo menos 6 caracteres', 'error');
      return;
    }
    const fd = new FormData();
    fd.append('nova_senha', passwordForm.nova_senha);
    try {
      const res = await fetch(`${API_URL}/api/account/users/${userId}/password`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: fd
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      showMessage('Senha alterada!');
      setPasswordForm({ user_id: null, nova_senha: '' });
    } catch (e) {
      showMessage(e.message || 'Erro ao alterar senha', 'error');
    }
  };

  const deactivateUser = async (userId) => {
    if (!window.confirm('Desativar este usuário?')) return;
    try {
      const res = await fetch(`${API_URL}/api/account/users/${userId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      showMessage('Usuário desativado');
      loadProfile();
    } catch (e) {
      showMessage(e.message || 'Erro', 'error');
    }
  };

  // ── CONVITES DE EQUIPE (#81) ─────────────────────────────────────────────
  const loadInvites = async () => {
    setInvitesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/account/invites`, {
        headers: authService.getAuthHeaders()
      });
      if (!res.ok) throw new Error();
      setInvites(await res.json());
    } catch (e) {
      // silencioso — a lista de pendentes é auxiliar
    } finally {
      setInvitesLoading(false);
    }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('email', newInvite.email);
      fd.append('role', newInvite.role);
      const res = await fetch(`${API_URL}/api/account/invites`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: fd
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      showMessage('Convite enviado por email!');
      setNewInvite({ email: '', role: 'operador' });
      loadInvites();
    } catch (e) {
      showMessage(e.message || 'Erro ao enviar convite', 'error');
    } finally {
      setSaving(false);
    }
  };

  const revokeInvite = async (inviteId) => {
    if (!window.confirm('Revogar este convite? O link deixa de funcionar.')) return;
    try {
      const res = await fetch(`${API_URL}/api/account/invites/${inviteId}/revoke`, {
        method: 'POST',
        headers: authService.getAuthHeaders()
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      showMessage('Convite revogado');
      loadInvites();
    } catch (e) {
      showMessage(e.message || 'Erro', 'error');
    }
  };

  // ── CERTIFICAÇÕES ─────────────────────────────────────────────────────────

  const addCert = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(newCert).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const res = await fetch(`${API_URL}/api/account/certifications`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: fd
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      showMessage('Certificação adicionada!');
      setNewCert({ certificacao: '', orgao_certificador: '', numero_certificado: '', validade: '' });
      loadProfile();
    } catch (e) {
      showMessage(e.message || 'Erro', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCert = async (certId) => {
    if (!window.confirm('Remover esta certificação?')) return;
    try {
      const res = await fetch(`${API_URL}/api/account/certifications/${certId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      showMessage('Certificação removida');
      loadProfile();
    } catch (e) {
      showMessage(e.message || 'Erro', 'error');
    }
  };

  if (loading) return (
    <div className="account-settings">
      <StateBlock kind="loading" message="Carregando..." />
    </div>
  );

  return (
    <div className="account-settings">
      <PageHeader
        title="Configurações da Conta"
        subtitle="Gerencie os dados da sua empresa, usuários e certificações"
      />

      {/* Toast */}
      {message && (
        <div className={`account-toast account-toast--${message.type}`}>
          <span className="material-symbols-outlined">
            {message.type === 'error' ? 'error' : 'check_circle'}
          </span>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="account-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`account-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-symbols-outlined">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="account-content">

        {/* ── EMPRESA ── */}
        {activeTab === 'empresa' && (
          <form onSubmit={saveEmpresa} className="account-form">
            {/* Logo */}
            <div className="account-section">
              <h3>Logotipo</h3>
              <div className="logo-upload-area">
                <div className="logo-preview" onClick={() => isAdmin && logoInputRef.current?.click()}>
                  {logoPreview
                    ? <img src={logoPreview} alt="Logo" />
                    : <span className="material-symbols-outlined logo-placeholder">add_photo_alternate</span>
                  }
                  {isAdmin && <div className="logo-overlay"><span className="material-symbols-outlined">edit</span></div>}
                </div>
                <div className="logo-info">
                  <p>PNG, JPG, SVG ou WEBP — máximo 2MB</p>
                  <p>Recomendado: 400×200px, fundo transparente</p>
                  <p>Aparece no cabeçalho dos relatórios enviados aos clientes</p>
                  {isAdmin && (
                    <Button type="button" variant="secondary" size="sm" icon="upload" style={{ marginTop: '8px' }} onClick={() => logoInputRef.current?.click()}>
                      Enviar Logo
                    </Button>
                  )}
                </div>
                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
              </div>
            </div>

            {/* Dados da empresa */}
            <div className="account-section">
              <h3>Dados da Empresa</h3>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Razão Social *</label>
                  <input name="razao_social" value={empresaForm.razao_social || ''} onChange={handleEmpresaChange} disabled={!isAdmin} required />
                </div>
                <div className="form-group">
                  <label>Nome Fantasia</label>
                  <input name="nome_fantasia" value={empresaForm.nome_fantasia || ''} onChange={handleEmpresaChange} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label>CNPJ</label>
                  <input
                    name="cnpj"
                    value={empresaForm.cnpj || ''}
                    onChange={handleEmpresaChange}
                    disabled={!isAdmin}
                    placeholder="00.000.000/0001-00"
                    inputMode="numeric"
                    maxLength={18}
                    aria-invalid={cnpjInvalido}
                    className={cnpjInvalido ? 'input-invalido' : undefined}
                  />
                  {cnpjInvalido && (
                    <span className="field-error" role="alert">CNPJ inválido — verifique os dígitos.</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Inscrição Estadual</label>
                  <input name="inscricao_estadual" value={empresaForm.inscricao_estadual || ''} onChange={handleEmpresaChange} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label>Indústria</label>
                  <select
                    name="industria"
                    value={empresaForm.industria || ''}
                    onChange={handleEmpresaChange}
                    disabled={!isAdmin}
                  >
                    <option value="">— Não especificada —</option>
                    {industrias.map((ind) => (
                      <option key={ind.codigo} value={ind.codigo}>{ind.nome}</option>
                    ))}
                    {/* #181: valor salvo fora da lista (legado / fetch lento) ainda aparece */}
                    {empresaForm.industria
                      && !industrias.some((i) => i.codigo === empresaForm.industria)
                      && (
                        <option value={empresaForm.industria}>{empresaForm.industria}</option>
                      )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Telefone</label>
                  <input name="telefone" value={empresaForm.telefone || ''} onChange={handleEmpresaChange} disabled={!isAdmin} placeholder="(11) 99999-9999" inputMode="numeric" maxLength={15} />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input name="website" value={empresaForm.website || ''} onChange={handleEmpresaChange} disabled={!isAdmin} placeholder="https://www.suaempresa.com.br" />
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="account-section">
              <h3>Endereço</h3>
              <div className="form-grid-2">
                <div className="form-group form-group--wide">
                  <label>Logradouro</label>
                  <input name="endereco_logradouro" value={empresaForm.endereco_logradouro || ''} onChange={handleEmpresaChange} disabled={!isAdmin} placeholder="Rua, Avenida, etc." />
                </div>
                <div className="form-group">
                  <label>Número</label>
                  <input name="endereco_numero" value={empresaForm.endereco_numero || ''} onChange={handleEmpresaChange} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label>Complemento</label>
                  <input name="endereco_complemento" value={empresaForm.endereco_complemento || ''} onChange={handleEmpresaChange} disabled={!isAdmin} placeholder="Sala, Bloco, etc." />
                </div>
                <div className="form-group">
                  <label>Bairro</label>
                  <input name="endereco_bairro" value={empresaForm.endereco_bairro || ''} onChange={handleEmpresaChange} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label>Cidade</label>
                  <input name="endereco_cidade" value={empresaForm.endereco_cidade || ''} onChange={handleEmpresaChange} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select name="endereco_estado" value={empresaForm.endereco_estado || ''} onChange={handleEmpresaChange} disabled={!isAdmin}>
                    <option value="">UF</option>
                    {ESTADOS_BR.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>CEP</label>
                  <input name="endereco_cep" value={empresaForm.endereco_cep || ''} onChange={handleEmpresaChange} disabled={!isAdmin} placeholder="00000-000" inputMode="numeric" maxLength={9} />
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="form-actions">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar Dados da Empresa'}
                </Button>
              </div>
            )}
          </form>
        )}

        {/* ── USUÁRIOS ── */}
        {activeTab === 'usuarios' && (
          <div className="account-form">
            {/* Lista de usuários */}
            <div className="account-section">
              <h3>Usuários Ativos</h3>
              <div className="users-list">
                {profile?.users?.map(u => (
                  <div key={u.id} className="user-card">
                    <div className="user-avatar">
                      {u.nome.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-info">
                      <strong>{u.nome}</strong>
                      <span>{u.email}</span>
                      <span className={`role-badge role-${u.role}`}>{u.role}</span>
                    </div>
                    <div className="user-actions">
                      {/* Alterar senha */}
                      {(isAdmin || u.id === user?.id) && (
                        passwordForm.user_id === u.id ? (
                          <div className="inline-password">
                            <input
                              type="password"
                              placeholder="Nova senha (mín. 6 chars)"
                              value={passwordForm.nova_senha}
                              onChange={e => setPasswordForm({ ...passwordForm, nova_senha: e.target.value })}
                            />
                            <button className="btn-sm btn-primary" onClick={() => changePassword(u.id)}>Salvar</button>
                            <button className="btn-sm btn-ghost" onClick={() => setPasswordForm({ user_id: null, nova_senha: '' })} title="Cancelar">
                              <span className="material-symbols-outlined">close</span>
                            </button>
                          </div>
                        ) : (
                          <button className="btn-sm btn-ghost" onClick={() => setPasswordForm({ user_id: u.id, nova_senha: '' })}>
                            <span className="material-symbols-outlined">lock_reset</span>
                          </button>
                        )
                      )}
                      {/* Desativar (apenas admin, não pode desativar a si mesmo) */}
                      {isAdmin && u.id !== user?.id && (
                        <button className="btn-sm btn-danger" onClick={() => deactivateUser(u.id)}>
                          <span className="material-symbols-outlined">person_off</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Criar novo usuário */}
            {isAdmin && (
              <div className="account-section">
                <h3>Adicionar Usuário</h3>
                <form onSubmit={createUser}>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label>Nome</label>
                      <input value={newUser.nome} onChange={e => setNewUser({ ...newUser, nome: e.target.value })} required placeholder="Nome completo" />
                    </div>
                    <div className="form-group">
                      <label>E-mail</label>
                      <input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required placeholder="email@empresa.com" />
                    </div>
                    <div className="form-group">
                      <label>Senha inicial</label>
                      <input type="password" value={newUser.senha} onChange={e => setNewUser({ ...newUser, senha: e.target.value })} required placeholder="Mínimo 6 caracteres" />
                    </div>
                    <div className="form-group">
                      <label>Perfil</label>
                      <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                        <option value="operador">Operador — registra NCs</option>
                        <option value="admin">Admin — gerencia tudo</option>
                        <option value="auditor">Auditor — somente leitura</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-actions">
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Criando...' : 'Criar Usuário'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* #81 — Convidar por email: o colega recebe um link e define a
                própria senha (sem o admin digitar senha por ele). */}
            {isAdmin && (
              <div className="account-section">
                <h3>Convidar por email</h3>
                <p className="section-hint" style={{ marginTop: '-0.25rem', color: '#64748b', fontSize: '0.85rem' }}>
                  O convidado recebe um link por email e cria a própria senha. O convite expira em 7 dias.
                </p>
                <form onSubmit={createInvite}>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label>E-mail do convidado</label>
                      <input type="email" value={newInvite.email} onChange={e => setNewInvite({ ...newInvite, email: e.target.value })} required placeholder="colega@empresa.com" />
                    </div>
                    <div className="form-group">
                      <label>Perfil</label>
                      <select value={newInvite.role} onChange={e => setNewInvite({ ...newInvite, role: e.target.value })}>
                        <option value="operador">Operador — registra NCs</option>
                        <option value="admin">Admin — gerencia tudo</option>
                        <option value="auditor">Auditor — somente leitura</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-actions">
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Enviando...' : 'Enviar convite'}
                    </Button>
                  </div>
                </form>

                {invites.length > 0 && (
                  <div className="users-list" style={{ marginTop: '1rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#334155' }}>Convites pendentes</h4>
                    {invites.map(inv => (
                      <div key={inv.id} className="user-row">
                        <div>
                          <div className="user-email">{inv.email}</div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            <span className={`role-badge role-${inv.role}`}>{inv.role}</span>
                            {inv.status === 'expired' ? ' · expirado' : ' · aguardando aceite'}
                          </div>
                        </div>
                        <button type="button" className="link-danger" onClick={() => revokeInvite(inv.id)}>
                          Revogar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {invitesLoading && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Carregando convites…</p>}
              </div>
            )}
          </div>
        )}

        {/* ── CERTIFICAÇÕES ── */}
        {activeTab === 'certificacoes' && (
          <div className="account-form">
            <div className="account-section">
              <h3>Certificações da Empresa</h3>
              {profile?.certifications?.length === 0 && (
                <StateBlock kind="empty" icon="verified" message="Nenhuma certificação cadastrada." />
              )}
              <div className="certs-list">
                {profile?.certifications?.map(c => (
                  <div key={c.id} className="cert-card">
                    <div className="cert-icon">
                      <span className="material-symbols-outlined">verified</span>
                    </div>
                    <div className="cert-info">
                      <strong>{c.certificacao}</strong>
                      {c.orgao_certificador && <span>Órgão: {c.orgao_certificador}</span>}
                      {c.numero_certificado && <span>Nº {c.numero_certificado}</span>}
                      {c.validade && (
                        <span className={new Date(c.validade) < new Date() ? 'cert-expired' : 'cert-valid'}>
                          Validade: {new Date(c.validade).toLocaleDateString('pt-BR')}
                          {new Date(c.validade) < new Date() && (
                            <>
                              {' '}
                              <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom' }}>warning</span>
                              {' Vencida'}
                            </>
                          )}
                        </span>
                      )}
                    </div>
                    {isAdmin && (
                      <button className="btn-sm btn-danger" onClick={() => deleteCert(c.id)}>
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {isAdmin && (
              <div className="account-section">
                <h3>Adicionar Certificação</h3>
                <form onSubmit={addCert}>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label>Certificação *</label>
                      <input value={newCert.certificacao} onChange={e => setNewCert({ ...newCert, certificacao: e.target.value })} required placeholder="Ex: ISO 9001:2015, IATF 16949" />
                    </div>
                    <div className="form-group">
                      <label>Órgão Certificador</label>
                      <input value={newCert.orgao_certificador} onChange={e => setNewCert({ ...newCert, orgao_certificador: e.target.value })} placeholder="Ex: Bureau Veritas, DNV" />
                    </div>
                    <div className="form-group">
                      <label>Número do Certificado</label>
                      <input value={newCert.numero_certificado} onChange={e => setNewCert({ ...newCert, numero_certificado: e.target.value })} placeholder="Ex: BV-12345-BR" />
                    </div>
                    <div className="form-group">
                      <label>Validade</label>
                      <input type="date" value={newCert.validade} onChange={e => setNewCert({ ...newCert, validade: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-actions">
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Salvando...' : 'Adicionar Certificação'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* ── NUMERAÇÃO DE NCs (C1) ── */}
        {activeTab === 'numeracao' && (
          <div className="account-form">
            <div className="account-section">
              <h3>Configuração de Numeração de NCs</h3>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                Defina o padrão de numeração e o próximo número que será emitido para o ano corrente.
                Exemplos comuns: <code>RNC-{'{'}YEAR{'}'}-{'{'}NUMBER:04d{'}'}</code> (gera <code>RNC-2026-0006</code>),
                ou <code>RNC-{'{'}NUMBER:03d{'}'}/{'{'}YEAR{'}'}</code> (gera <code>RNC-006/2026</code>).
              </p>

              {!isAdmin && (
                <div className="account-message warning" style={{ marginBottom: '12px' }}>
                  Apenas administradores podem alterar a numeração.
                </div>
              )}

              <div className="account-form-grid">
                <div className="account-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Formato (template)</label>
                  <input
                    type="text"
                    value={numberingForm.formato}
                    onChange={(e) => setNumberingForm({ ...numberingForm, formato: e.target.value })}
                    placeholder="RNC-{YEAR}-{NUMBER:04d}"
                    disabled={!isAdmin}
                  />
                  <small style={{ color: '#64748b', fontSize: '12px' }}>
                    Placeholders: <code>{'{YEAR}'}</code>, <code>{'{YEAR:02d}'}</code>,{' '}
                    <code>{'{NUMBER}'}</code>, <code>{'{NUMBER:04d}'}</code>, <code>{'{NUMBER:03d}'}</code>
                  </small>
                </div>

                <div className="account-form-group">
                  <label>
                    {numberingForm.continuo
                      ? 'Próximo número (contínuo)'
                      : `Próximo número (ano ${ncNumbering?.ano || new Date().getFullYear()})`}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={numberingForm.proximo_numero}
                    onChange={(e) => setNumberingForm({ ...numberingForm, proximo_numero: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>

                {/* #135: numeração contínua (sem reset anual) */}
                <div className="account-form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isAdmin ? 'pointer' : 'default' }}>
                    <input
                      type="checkbox"
                      checked={!!numberingForm.continuo}
                      onChange={(e) => setNumberingForm({ ...numberingForm, continuo: e.target.checked })}
                      disabled={!isAdmin}
                      style={{ width: 'auto' }}
                    />
                    Numeração contínua (não reinicia a cada ano)
                  </label>
                  <small style={{ color: '#64748b', fontSize: '12px' }}>
                    Quando ativada, o contador segue subindo na virada de ano (ex.: …0098, 0099, 0100…).
                    O <code>{'{YEAR}'}</code> do formato, se usado, continua mostrando o ano de criação.
                  </small>
                </div>

                <div className="account-form-group">
                  <label>Pré-visualização</label>
                  <div style={{
                    padding: '10px 14px',
                    background: '#ecfdf5',
                    border: '1px solid #10b981',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#065f46',
                  }}>
                    {formatPreview(
                      numberingForm.formato,
                      ncNumbering?.ano || new Date().getFullYear(),
                      parseInt(numberingForm.proximo_numero, 10) || 1
                    ) || '—'}
                  </div>
                </div>
              </div>

              {isAdmin && (
                <Button
                  onClick={saveNcNumbering}
                  disabled={saving}
                  style={{ marginTop: '16px' }}
                >
                  {saving ? 'Salvando...' : 'Salvar configuração'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ── #67 — ESCALA DE SEVERIDADE (rótulos + cores da casa) ── */}
        {activeTab === 'severidade' && (
          <div className="account-form">
            <div className="account-section">
              <h3>Escala de severidade</h3>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                Renomeie os 4 níveis de severidade com a nomenclatura da sua empresa
                (ex.: <code>A/B/C/D</code> ou <code>1/2/3/4</code>) e, se quiser, escolha a cor de cada um.
                Isso muda só a <strong>exibição</strong> — a análise da IA e os relatórios continuam
                tratando os mesmos 4 níveis por baixo.
              </p>

              {!isAdmin && (
                <div className="account-message warning" style={{ marginBottom: '12px' }}>
                  Apenas administradores podem alterar a escala de severidade.
                </div>
              )}

              {(severidadeForm || []).map((n, idx) => {
                const refNome = { critica: 'Crítica', alta: 'Alta', media: 'Média', baixa: 'Baixa' }[n.nivel];
                const update = (patch) => setSeveridadeForm((prev) =>
                  prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
                return (
                  <div key={n.nivel} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '10px 0', borderBottom: idx < 3 ? '1px solid #f0f2f5' : 'none' }}>
                    <span style={{ minWidth: '70px', fontSize: '13px', color: '#64748b' }}>{refNome}</span>
                    <input
                      type="text"
                      value={n.rotulo}
                      maxLength={20}
                      onChange={(e) => update({ rotulo: e.target.value })}
                      placeholder={refNome}
                      disabled={!isAdmin}
                      style={{ flex: '1 1 140px', minWidth: '120px', padding: '8px 12px', border: '1px solid #d0d7de', borderRadius: '8px', fontSize: '14px' }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3d4f61', cursor: isAdmin ? 'pointer' : 'default' }}>
                      <input
                        type="checkbox"
                        checked={n.corAtiva}
                        onChange={(e) => update({ corAtiva: e.target.checked })}
                        disabled={!isAdmin}
                        style={{ width: 'auto' }}
                      />
                      Cor personalizada
                    </label>
                    <input
                      type="color"
                      value={n.cor}
                      onChange={(e) => update({ cor: e.target.value, corAtiva: true })}
                      disabled={!isAdmin || !n.corAtiva}
                      title={n.corAtiva ? 'Cor do badge' : 'Marque "Cor personalizada" para editar'}
                      style={{ width: '40px', height: '32px', padding: '2px', border: '1px solid #d0d7de', borderRadius: '6px', cursor: (isAdmin && n.corAtiva) ? 'pointer' : 'default', opacity: n.corAtiva ? 1 : 0.4 }}
                    />
                    <span
                      style={{
                        padding: '4px 12px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        background: n.corAtiva ? n.cor : '#eef2f6',
                        color: n.corAtiva ? '#fff' : '#64748b',
                      }}
                    >
                      {n.rotulo || refNome}
                    </span>
                  </div>
                );
              })}

              {isAdmin && severidadeForm && (
                <Button
                  onClick={saveSeveridadeConfig}
                  disabled={saving}
                  style={{ marginTop: '16px' }}
                >
                  {saving ? 'Salvando...' : 'Salvar escala'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ── ALERTAS DE AÇÕES EM ATRASO (Sessão 24 #59 — Felipe F1.3 Sessão 3) ── */}
        {/* #323 — a aba inteira (CC casa cheia + lembrete de eficácia) é camada de
            GESTÃO Pro+. Tier abaixo do Pro ⇒ substitui pelo upsell (bloco abaixo).
            Flag OFF ⇒ alertasGestaoLiberado=true ⇒ conteúdo normal (inerte). */}
        {activeTab === 'alertas' && alertasGestaoLiberado && (
          <div className="account-form">
            <div className="account-section">
              <h3>Destinatários de alertas</h3>
              <p className="account-help">
                E-mails que recebem cópia dos alertas automáticos de ações em
                atraso desta conta (D-3 lembrete, D-0 atenção, D+1 atrasada).
                O responsável direto de cada ação (preenchido no NCEditor) já
                recebe automaticamente — esta lista é o destino adicional
                "casa cheia" do tenant.
              </p>
              <p className="account-help" style={{ marginTop: '8px' }}>
                <strong>Caso de uso:</strong> gerente da qualidade em férias passa
                a função pro substituto sem mexer em código. É só atualizar a
                lista aqui.
              </p>

              {destinatariosAlertas.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '16px 0' }}>
                  Nenhum destinatário cadastrado.{' '}
                  {isAdmin
                    ? 'Adicione um e-mail abaixo.'
                    : 'Apenas administradores podem alterar a lista.'}
                </p>
              ) : (
                <ul className="destinatarios-lista">
                  {destinatariosAlertas.map((email) => (
                    <li key={email} className="destinatarios-item">
                      <span className="material-symbols-outlined">mail</span>
                      <span className="destinatarios-email">{email}</span>
                      {isAdmin && (
                        <button
                          className="btn-icon-danger"
                          onClick={() => removeDestinatarioAlerta(email)}
                          disabled={saving}
                          title="Remover destinatário"
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {isAdmin && (
                <div className="destinatarios-add">
                  <input
                    type="email"
                    placeholder="email@empresa.com.br"
                    value={novoDestinatario}
                    onChange={(e) => setNovoDestinatario(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addDestinatarioAlerta();
                      }
                    }}
                    disabled={saving}
                  />
                  <Button
                    size="sm"
                    onClick={addDestinatarioAlerta}
                    disabled={saving || !novoDestinatario.trim()}
                  >
                    Adicionar
                  </Button>
                </div>
              )}
            </div>

            {/* #104 (Sessão 32): destinatários GERENCIAIS — avaliação de eficácia */}
            <div className="account-section">
              <h3>Destinatários da avaliação de eficácia</h3>
              <p className="account-help">
                E-mails que recebem o lembrete <strong>3 dias antes</strong> da data
                prevista para avaliar a eficácia das ações de uma NC. É um aviso
                <strong> gerencial</strong> (quem decide se a ação resolveu de verdade)
                — separado da lista operacional de ações em atraso acima.
              </p>
              <p className="account-help" style={{ marginTop: '8px' }}>
                Se esta lista ficar vazia, o lembrete vai pro(s) e-mail(s) do
                administrador da conta.
              </p>

              {destinatariosEficacia.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '16px 0' }}>
                  Nenhum destinatário gerencial cadastrado.{' '}
                  {isAdmin
                    ? 'Adicione um e-mail abaixo (ou o lembrete cai pro admin).'
                    : 'Apenas administradores podem alterar a lista.'}
                </p>
              ) : (
                <ul className="destinatarios-lista">
                  {destinatariosEficacia.map((email) => (
                    <li key={email} className="destinatarios-item">
                      <span className="material-symbols-outlined">verified</span>
                      <span className="destinatarios-email">{email}</span>
                      {isAdmin && (
                        <button
                          className="btn-icon-danger"
                          onClick={() => removeDestinatarioEficacia(email)}
                          disabled={saving}
                          title="Remover destinatário"
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {isAdmin && (
                <div className="destinatarios-add">
                  <input
                    type="email"
                    placeholder="gerente.qualidade@empresa.com.br"
                    value={novoDestinatarioEficacia}
                    onChange={(e) => setNovoDestinatarioEficacia(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addDestinatarioEficacia();
                      }
                    }}
                    disabled={saving}
                  />
                  <Button
                    size="sm"
                    onClick={addDestinatarioEficacia}
                    disabled={saving || !novoDestinatarioEficacia.trim()}
                  >
                    Adicionar
                  </Button>
                </div>
              )}
            </div>

            {/* #243 follow-up — opt-in do relógio de SLA 8D (gated Pro+) */}
            {sla8d !== null && (
              <div className="account-section">
                <h3>Relógio de SLA 8D (automotivo)</h3>
                <p className="account-help">
                  Prazos padrão do 8D contados da <strong>abertura</strong> de cada NC:
                  contenção em <strong>24 horas</strong> e resolução em{' '}
                  <strong>10 dias úteis</strong>. Com o relógio ativo, a NC mostra o
                  countdown dos prazos e os destinatários de alertas acima recebem os
                  avisos de vencimento (D-3, D-0, D+1). Opcional — pensado para
                  fornecedores da cadeia automotiva (IATF); não ative se os prazos
                  fixos não fazem sentido no seu processo.
                </p>

                {sla8d.elegivel ? (
                  <>
                    {!isAdmin && (
                      <div className="account-message warning" style={{ marginBottom: '12px' }}>
                        Apenas administradores podem alterar esta configuração.
                      </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isAdmin ? 'pointer' : 'default', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={!!sla8d.sla_8d_habilitado}
                        onChange={(e) => toggleSla8d(e.target.checked)}
                        disabled={!isAdmin || sla8dSaving}
                        style={{ width: 'auto' }}
                      />
                      Ativar os prazos de SLA 8D para as NCs desta conta
                    </label>
                  </>
                ) : (
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: '12px 0 0' }}>
                    Disponível a partir do plano <strong>Pro</strong>.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* #323 — upsell no lugar da aba Alertas quando o tier está abaixo do Pro. */}
        {activeTab === 'alertas' && !alertasGestaoLiberado && (
          <div className="account-form">
            <UpsellPro
              icone="notifications"
              titulo="Alertas de gestão"
              descricao="Copie a equipe (a lista 'casa cheia') nos alertas de ações em atraso e receba o lembrete de avaliação de eficácia 3 dias antes do prazo. Camada gerencial para quem coordena a qualidade — o responsável direto de cada ação continua sendo avisado normalmente."
            />
          </div>
        )}

        {/* ── ASSINATURA ── */}
        {activeTab === 'assinatura' && subscription && (
          <div className="account-form">
            <div className="account-section">
              <h3>Plano Atual</h3>
              <div className="subscription-current">
                <div className="plan-badge">
                  <span className="material-symbols-outlined">workspace_premium</span>
                  <div>
                    <strong>{subscription.plan_info?.nome || subscription.plan}</strong>
                    <span className={`status-badge status-${subscription.status}`}>{subscription.status}</span>
                  </div>
                </div>
                <div className="plan-limits">
                  <div>
                    <span className="material-symbols-outlined">description</span>
                    NCs/mês: {subscription.plan_info?.ncs_mes === -1 ? 'Ilimitado' : subscription.plan_info?.ncs_mes}
                  </div>
                  <div>
                    <span className="material-symbols-outlined">group</span>
                    Usuários: {subscription.plan_info?.usuarios === -1 ? 'Ilimitado' : subscription.plan_info?.usuarios}
                  </div>
                </div>
              </div>
            </div>

            <div className="account-section">
              <h3>Planos Disponíveis</h3>
              {subscription.status === 'pending' && (
                <p className="subscription-note subscription-note--pending">
                  <span className="material-symbols-outlined">schedule</span>
                  Há um pagamento pendente. Assim que ele for confirmado, seu plano é ativado automaticamente — ou clique em "Assinar" para gerar um novo link.
                </p>
              )}
              <div className="plans-grid">
                {Object.entries(subscription.available_plans || {}).map(([key, plan]) => {
                  const selfServe = PLANOS_CHECKOUT.includes(key);
                  const temAncora = plan.preco_regular && plan.preco_regular > plan.preco;
                  return (
                  <div key={key} className={`plan-card ${subscription.plan === key ? 'plan-card--active' : ''}`}>
                    <h4>{plan.nome}</h4>
                    <div className="plan-price">
                      {plan.preco === 0 ? 'Grátis' : plan.preco ? (
                        <>
                          {temAncora && <span className="plan-price-regular">R$ {plan.preco_regular}</span>}
                          R$ {plan.preco}/mês
                        </>
                      ) : 'Sob consulta'}
                    </div>
                    <ul>
                      {/* O grátis não é uma cota recorrente de NCs: é Registro ilimitado
                          + 1 degustação de Investigação (modelo #226/#161). O "10 NCs/mês"
                          do dict do backend era um número de display que não enforça nada. */}
                      {key === 'trial' ? (
                        <>
                          <li>Registro de NCs ilimitado</li>
                          <li>1ª Investigação completa grátis</li>
                          <li>{plan.usuarios === -1 ? 'Usuários ilimitados' : `${plan.usuarios} usuários`}</li>
                        </>
                      ) : (
                        <>
                          <li>{plan.ncs_mes === -1 ? 'NCs ilimitadas' : `${plan.ncs_mes} NCs/mês`}</li>
                          <li>{plan.usuarios === -1 ? 'Usuários ilimitados' : `${plan.usuarios} usuários`}</li>
                        </>
                      )}
                    </ul>
                    {/* trial não é assinável nem objeto de contato → sem CTA */}
                    {subscription.plan !== key && key !== 'trial' && (
                      selfServe ? (
                        <Button size="sm"
                          disabled={assinandoPlano === key}
                          onClick={() => setConfirmAssinarPlano(key)}>
                          {assinandoPlano === key ? 'Gerando…' : 'Assinar'}
                        </Button>
                      ) : (
                        <Button size="sm"
                          onClick={() => window.open('https://qaasa.com.br/planos#contato', '_blank', 'noopener')}>
                          Falar com a gente
                        </Button>
                      )
                    )}
                    {subscription.plan === key && (
                      <span className="plan-current-label">Plano atual</span>
                    )}
                  </div>
                  );
                })}
              </div>
              <p className="subscription-note">
                <span className="material-symbols-outlined">info</span>
                Assinatura paga via Pix ou boleto. Seu plano é ativado automaticamente assim que o pagamento for confirmado. Planos maiores (Enterprise): fale com a gente.
              </p>
              {/* #227 — direito de arrependimento (art. 49 CDC). Copy honesta, igual à
                  da landing /planos ("menos o que você já usou"). Reembolso operado
                  manualmente por enquanto. */}
              <p className="subscription-note">
                <span className="material-symbols-outlined">verified_user</span>
                <span><strong>Garantia de 7 dias.</strong> Assinou e mudou de ideia? Avise em até 7 dias da sua 1ª assinatura e devolvemos o valor pago, menos o que você já usou. Em linha com o direito de arrependimento (art. 49 do CDC).</span>
              </p>

              {/* Confirmação antes de sair do app pro gateway de pagamento (Asaas). */}
              <ConfirmModal
                open={!!confirmAssinarPlano}
                title="Ir para o pagamento"
                icon="credit_card"
                confirmLabel="Continuar para o pagamento"
                cancelLabel="Cancelar"
                loading={assinandoPlano === confirmAssinarPlano}
                onConfirm={confirmarAssinatura}
                onCancel={() => setConfirmAssinarPlano(null)}>
                {(() => {
                  const p = subscription?.available_plans?.[confirmAssinarPlano];
                  return (
                    <>
                      <p>
                        Você vai assinar o plano <strong>{p?.nome || confirmAssinarPlano}</strong>
                        {p?.preco ? <> por <strong>R$ {p.preco}/mês</strong></> : null}.
                      </p>
                      <p>
                        Ao continuar, você será levado a uma <strong>página de pagamento segura</strong> do
                        nosso parceiro (Asaas), onde poderá pagar por <strong>Pix ou boleto</strong>. Seu
                        plano é ativado automaticamente assim que o pagamento for confirmado.
                      </p>
                    </>
                  );
                })()}
              </ConfirmModal>
            </div>
          </div>
        )}

        {activeTab === 'consultor' && isAdmin && (
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>
              Consultor parceiro
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Gerencie o acesso do consultor externo vinculado à sua conta.
            </p>

            {consultorAccessLoading && (
              <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Carregando...</div>
            )}

            {consultorAccess && !consultorAccess._error && !consultorAccessLoading && (
              <>
                {consultorAccess.consultor ? (
                  <div style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                    padding: '1rem 1.25rem', marginBottom: '1.25rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <span className="material-symbols-outlined" style={{ color: '#667eea', fontSize: '22px', marginTop: '2px' }}>handshake</span>
                      <div>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{consultorAccess.consultor.nome}</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.15rem' }}>
                          {consultorAccess.consultor.codigo} · {consultorAccess.consultor.email}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                    padding: '1rem 1.25rem', marginBottom: '1.25rem',
                    color: '#64748b', fontSize: '0.875rem',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>info</span>
                    Nenhum consultor vinculado. Entre em contato com a Qaa.sa para associar um consultor parceiro à sua conta.
                  </div>
                )}

                {consultorAccess.consultor && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1rem', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                        Permitir visualização das NCs
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                        O consultor poderá ler as Não-Conformidades desta conta (somente leitura).
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleConsultorAcesso(!consultorAccess.consultor_pode_ver_ncs)}
                      disabled={consultorAccessSaving}
                      style={{
                        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: consultorAccessSaving ? 'not-allowed' : 'pointer',
                        background: consultorAccess.consultor_pode_ver_ncs ? '#667eea' : '#cbd5e1',
                        position: 'relative', transition: 'background .2s', flexShrink: 0,
                      }}
                      title={consultorAccess.consultor_pode_ver_ncs ? 'Revogar acesso' : 'Permitir acesso'}
                    >
                      <span style={{
                        position: 'absolute', top: 2, left: consultorAccess.consultor_pode_ver_ncs ? 22 : 2,
                        width: 20, height: 20, borderRadius: '50%', background: '#fff',
                        transition: 'left .2s', display: 'block',
                      }} />
                    </button>
                  </div>
                )}
              </>
            )}

            {consultorAccess && consultorAccess._error && (
              <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>
                Erro ao carregar dados do consultor. Tente recarregar a página.
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
